// RulebookPage — ルールブック (#rulebook, blueprint v2 Phase 2, 2026-07-05)
//
// 学習内容 (rules 4テーブル) を1件=1レコードとして「由来→内容→成績→適用先」で
// 閲覧・構造化編集・無効化・テスト実行 (dry-run)・feedback するページ。
//   - 編集は構造化フォームのみ (裁定3: Worker 直接 PATCH / CAS expected_version、409は再読込促し)
//   - ドメイン語彙は schema-v76 正本 (bank_pattern 等)。UIタブは固定写像 (裁定4)
//   - 「要注意」は status でなく導出フラグ (差戻し率から計算)
// デザインは JournalReviewPage のパレットC準拠 (Ledger UI Kit 先行流用・BatchesPage 同流儀)。

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Container, Button, InlineSpinner, MessageBlock } from '@freee_jp/vibes';
import {
  MdRefresh, MdSync, MdPushPin, MdWarningAmber, MdExpandMore, MdEditNote,
  MdHistory, MdScience, MdCheckCircle, MdMenuBook, MdAdd, MdInfoOutline,
} from 'react-icons/md';
import {
  fetchRulebookRules, fetchRulebookRuleDetail, patchRulebookRule, createRulebookRule,
  disableRulebookRule, enableRulebookRule, postRulebookFeedback,
  dryRunRulebookRule, fetchRulebookStats, syncRulebookLearning,
  fetchAccountItemsMap, fetchSectionsMap, fetchTaxCodesMap, fetchPartnersMap,
  RulebookRule, RulebookRuleDetail, RulebookDryRunResult, RulebookStatsResponse,
} from '../api/client';

// ---------------------------------------------------------------------------
// パレットC (JournalReviewPage 正本)
// ---------------------------------------------------------------------------
const C = {
  ground: '#edf1f5', ink: '#1d2a3a', sub: '#647386', line: '#d9e0e7', lineDark: '#bdc8d3',
  navy: '#203a57', blueTint: '#f0f6ff', blueLine: '#b9d2ed', orangeTint: '#fff7ee', orangeLine: '#ecd2ae',
  good: '#267a62', goodBg: '#edf8f4', warn: '#a46a12', warnBg: '#fdf3e3',
  danger: '#b3261e', dangerBg: '#fdecea',
};
const FONT = '"Hiragino Sans","Yu Gothic UI","Meiryo",-apple-system,"Segoe UI",sans-serif';
const NUM = '"SFMono-Regular",Consolas,"Liberation Mono",Menlo,monospace';

const S = {
  page: { background: C.ground, minHeight: '100vh', paddingBottom: '3rem', color: C.ink, fontFamily: FONT, fontSize: 14 } as React.CSSProperties,
  sheet: { border: '1px solid #c8d1db', borderRadius: 10, background: '#fff', boxShadow: '0 9px 22px rgba(33,52,74,.08)', marginTop: 16, overflow: 'hidden' } as React.CSSProperties,
  top: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, minHeight: 58, padding: '10px 18px 10px 20px', color: '#fff', background: C.navy, flexWrap: 'wrap' } as React.CSSProperties,
  topTitle: { margin: 0, fontSize: 19, letterSpacing: '.025em', fontWeight: 700 } as React.CSSProperties,
  topCaption: { color: '#c9d6e5', fontSize: 12, fontWeight: 600 } as React.CSSProperties,
  headerChip: { padding: '4px 9px', border: '1px solid rgba(255,255,255,.28)', borderRadius: 4, color: '#e5eef8', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' } as React.CSSProperties,
  filterRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 18px 12px 20px', borderBottom: `1px solid ${C.line}`, background: '#fcfdfe' } as React.CSSProperties,
  filterLabel: { color: C.sub, fontSize: 11, fontWeight: 800 } as React.CSSProperties,
  filterInput: { height: 30, padding: '0 8px', border: `1px solid ${C.lineDark}`, borderRadius: 4, font: `600 12px ${FONT}`, color: '#3c4c5e', background: '#fff' } as React.CSSProperties,
  tabControl: { display: 'flex', alignItems: 'center', border: `1px solid ${C.lineDark}`, borderRadius: 5, overflow: 'hidden', background: '#fff', flexWrap: 'wrap' } as React.CSSProperties,
  list: { padding: '14px 18px 18px', background: '#f7f9fb' } as React.CSSProperties,
  tableWrap: { overflowX: 'auto', border: `1px solid ${C.lineDark}`, borderRadius: 5, background: '#fff' } as React.CSSProperties,
  th: { textAlign: 'left', padding: '7px 10px', borderBottom: `1px solid ${C.lineDark}`, fontSize: 10, color: C.sub, fontWeight: 800, letterSpacing: '.05em', background: '#f4f7fa', whiteSpace: 'nowrap' } as React.CSSProperties,
  td: { padding: '8px 10px', borderBottom: `1px solid ${C.line}`, fontSize: 12, verticalAlign: 'top' } as React.CSSProperties,
  badge: (bg: string, fg: string, bd: string): React.CSSProperties => ({ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 800, background: bg, color: fg, border: `1px solid ${bd}`, whiteSpace: 'nowrap' }),
  chip: { display: 'inline-flex', alignItems: 'center', padding: '1px 6px', marginRight: 4, marginBottom: 2, border: '1px solid #d7e2ef', borderRadius: 3, background: '#f3f8fd', color: '#476b8e', fontSize: 10, fontWeight: 800, whiteSpace: 'nowrap' } as React.CSSProperties,
  detailWrap: { borderTop: `1px solid ${C.lineDark}`, background: '#fbfcfd', padding: '12px 15px 15px' } as React.CSSProperties,
  secHead: { display: 'flex', alignItems: 'center', gap: 7, margin: '14px 0 7px', color: '#344b62', fontSize: 12.5, fontWeight: 800 } as React.CSSProperties,
  box: { border: `1px solid ${C.lineDark}`, borderRadius: 5, background: '#fff', overflow: 'hidden' } as React.CSSProperties,
  kvKey: { padding: '6px 10px', borderBottom: `1px solid ${C.line}`, borderRight: `1px solid ${C.line}`, width: 190, color: C.sub, fontSize: 11, fontWeight: 800, background: '#f8fafc', verticalAlign: 'top', fontFamily: NUM } as React.CSSProperties,
  kvVal: { padding: '6px 10px', borderBottom: `1px solid ${C.line}`, fontSize: 12, verticalAlign: 'top', wordBreak: 'break-word' } as React.CSSProperties,
  formInput: { height: 28, padding: '0 8px', border: `1px solid ${C.lineDark}`, borderRadius: 4, font: `600 12px ${FONT}`, color: '#3c4c5e', background: '#fff', width: '100%', boxSizing: 'border-box' } as React.CSSProperties,
  smallBtn: { height: 28, padding: '0 11px', border: `1px solid ${C.lineDark}`, borderRadius: 4, background: '#fff', color: '#53657a', font: `700 12px ${FONT}`, cursor: 'pointer' } as React.CSSProperties,
  timelineItem: { display: 'flex', gap: 10, alignItems: 'flex-start', padding: '7px 12px', borderBottom: `1px solid ${C.line}`, fontSize: 12 } as React.CSSProperties,
  mono: { fontFamily: NUM, fontVariantNumeric: 'tabular-nums' } as React.CSSProperties,
  toast: { position: 'fixed', right: 18, bottom: 18, zIndex: 100, maxWidth: 420, padding: '11px 16px', borderRadius: 5, boxShadow: '0 9px 22px rgba(33,52,74,.18)', fontSize: 12.5, fontWeight: 700, border: '1px solid' } as React.CSSProperties,
  rawPre: { margin: 0, padding: 12, background: '#f8fafc', fontSize: 11, fontFamily: NUM, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all' } as React.CSSProperties,
};

const tabBtnSt = (active: boolean): React.CSSProperties => ({
  height: 30, padding: '0 12px', border: 0, borderLeft: `1px solid ${C.line}`, cursor: 'pointer',
  font: `700 12px ${FONT}`,
  color: active ? '#234e79' : '#53657a', background: active ? C.blueTint : '#fff',
  boxShadow: active ? 'inset 0 -2px 0 #467da9' : undefined,
});

// ---------------------------------------------------------------------------
// 語彙 (裁定4: domain/status の物理正本 → UI 表示は固定写像)
// ---------------------------------------------------------------------------
const DOMAIN_TABS: Array<{ key: string; label: string }> = [
  { key: '', label: '全て' },
  { key: 'bank_pattern', label: '銀行' },
  { key: 'invoice_layout', label: '請求書' },
  { key: 'amount_paradigm', label: '金額' },
  { key: 'billing_cadence', label: '周期' },
  { key: 'curated_default', label: '既定勘定' },
  { key: 'line_account_override', label: '科目上書き' },
  { key: 'vendor_default', label: '業者既定部門' },
  { key: 'section_alias', label: '宛名対応' },
  { key: 'knowledge', label: '知識' },
];
const DOMAIN_LABEL: Record<string, string> = {
  bank_pattern: '銀行', invoice_layout: '請求書', amount_paradigm: '金額',
  billing_cadence: '周期', curated_default: '既定勘定', line_account_override: '科目上書き',
  reader_weight: 'リーダー重み', routine: '定型',
  vendor_default: '業者既定部門', section_alias: '宛名対応', knowledge: '知識',
};
// knowledge domain は「機械が自動適用しない参考知識」— 一覧バッジ・詳細バナーで明示する
const KNOWLEDGE_NOTE = 'この「知識」は参考情報です — 機械 (自動仕訳・proposal 生成) は自動適用しません。人と AI が判断時の文脈として参照します';
const STATUS_META: Record<string, { label: string; bg: string; fg: string; bd: string }> = {
  active: { label: '有効', bg: C.goodBg, fg: C.good, bd: '#9fdbc7' },
  staging: { label: '候補', bg: C.blueTint, fg: '#33699e', bd: C.blueLine },
  disabled: { label: '無効', bg: '#f1f4f7', fg: '#5e7083', bd: '#ccd6df' },
  superseded: { label: '置換済', bg: '#f1f4f7', fg: '#5e7083', bd: '#ccd6df' },
};
const ORIGIN_LABEL: Record<string, string> = {
  learned_auto: '自動学習', learned_from_approval: '承認から学習', manual_ui: '手動編集(UI)',
  seeded: '初期投入', cli_proposed: 'CLI提案',
};
const RESULT_META: Record<string, { label: string; bg: string; fg: string; bd: string }> = {
  accepted: { label: '採用', bg: C.goodBg, fg: C.good, bd: '#9fdbc7' },
  overridden_by_user: { label: 'ユーザー上書き', bg: C.warnBg, fg: C.warn, bd: '#e3c68a' },
};
const VERDICT_META: Record<string, { label: string; bg: string; fg: string; bd: string }> = {
  correct: { label: '正しい', bg: C.goodBg, fg: C.good, bd: '#9fdbc7' },
  wrong: { label: '誤り', bg: C.dangerBg, fg: C.danger, bd: '#e6b0ac' },
  partial: { label: '部分的', bg: C.warnBg, fg: C.warn, bd: '#e3c68a' },
};

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------
const parseJsonSafe = (s: string | null | undefined): any => {
  if (!s) return null;
  try { return JSON.parse(s); } catch { return null; }
};
const fmtTs = (t: number | string | null | undefined): string => {
  if (t === null || t === undefined || t === '') return '—';
  let d: Date;
  if (typeof t === 'number') d = new Date(t < 1e12 ? t * 1000 : t);
  else if (/^\d+$/.test(String(t))) { const n = Number(t); d = new Date(n < 1e12 ? n * 1000 : n); }
  else d = new Date(t);
  if (Number.isNaN(d.getTime())) return String(t);
  const p = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};
const trunc = (s: string, n: number) => (s.length > n ? `${s.slice(0, n)}…` : s);
const scalarStr = (v: any): string => {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

interface NameMaps3 { accounts: Map<number, string>; sections: Map<number, string>; taxes: Map<number, string>; }
const EMPTY_MAPS: NameMaps3 = { accounts: new Map(), sections: new Map(), taxes: new Map() };

/** payload/match_keys のキーから ID→名称解決先のマスタを決める (勘定・部門・税のみ、裁定どおり ID 併記) */
const mapForKey = (key: string, maps: NameMaps3): Map<number, string> | null => {
  const k = key.toLowerCase();
  if (k === 'account_item_id' || k.endsWith('account_item_id')) return maps.accounts;
  if (k === 'section_id' || k.endsWith('section_id')) return maps.sections;
  if (k === 'tax_code' || k.endsWith('tax_code')) return maps.taxes;
  return null;
};
const resolveIdValue = (key: string, v: any, maps: NameMaps3): string => {
  const m = mapForKey(key, maps);
  if (m && (typeof v === 'number' || (typeof v === 'string' && /^\d+$/.test(v)))) {
    const name = m.get(Number(v));
    if (name) return `${name} (ID:${v})`;
    return `ID:${v}`;
  }
  return scalarStr(v);
};

// 導出フラグ「要注意」: 差戻し率 30% 以上 (裁定4: status ではなく導出)
const isAttention = (r: RulebookRule): boolean => {
  const a = Number(r.approve_count || 0); const j = Number(r.reject_count || 0);
  return j > 0 && j / Math.max(1, a + j) >= 0.3;
};

// ---------------------------------------------------------------------------
// 構造化フォーム (raw JSON 直編集の禁止: キー単位の型付き入力のみ)
// ---------------------------------------------------------------------------
interface EditField { key: string; kind: 'string' | 'number' | 'boolean' | 'json'; value: string; wasNull: boolean; }
const toFields = (obj: Record<string, any>): EditField[] =>
  Object.entries(obj || {}).map(([k, v]) => {
    if (typeof v === 'number') return { key: k, kind: 'number' as const, value: String(v), wasNull: false };
    if (typeof v === 'boolean') return { key: k, kind: 'boolean' as const, value: String(v), wasNull: false };
    if (v === null || v === undefined) {
      // null の ID/コード項目 (curated_default の section_id:null 等) は number 扱いにして
      // マスタピッカーを出す (空のまま保存すれば null に戻る = 往復無損失)
      const kl = k.toLowerCase();
      if (kl.endsWith('_id') || kl.endsWith('_code')) return { key: k, kind: 'number' as const, value: '', wasNull: true };
      return { key: k, kind: 'string' as const, value: '', wasNull: true };
    }
    if (typeof v === 'string') return { key: k, kind: 'string' as const, value: v, wasNull: false };
    return { key: k, kind: 'json' as const, value: JSON.stringify(v), wasNull: false };
  });
const fromFields = (fields: EditField[]): { ok: true; obj: Record<string, any> } | { ok: false; error: string } => {
  const obj: Record<string, any> = {};
  for (const f of fields) {
    const raw = f.value;
    if (f.kind === 'number') {
      if (raw.trim() === '') { obj[f.key] = null; continue; }
      const n = Number(raw);
      if (Number.isNaN(n)) return { ok: false, error: `「${f.key}」は数値で入力してください` };
      obj[f.key] = n;
    } else if (f.kind === 'boolean') {
      obj[f.key] = raw === 'true';
    } else if (f.kind === 'json') {
      try { obj[f.key] = JSON.parse(raw); }
      catch { return { ok: false, error: `「${f.key}」のJSONが不正です` }; }
    } else {
      obj[f.key] = raw === '' && f.wasNull ? null : raw;
    }
  }
  return { ok: true, obj };
};

const FieldInput: React.FC<{
  field: EditField; maps: NameMaps3; onChange: (v: string) => void;
}> = ({ field, maps, onChange }) => {
  const m = field.kind === 'number' ? mapForKey(field.key, maps) : null;
  if (m && m.size > 0) {
    const cur = field.value;
    const known = cur !== '' && m.has(Number(cur));
    const opts = Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1], 'ja'));
    return (
      <select style={S.formInput} value={cur} onChange={(e) => onChange(e.target.value)}>
        {cur === '' && <option value="">(未設定)</option>}
        {!known && cur !== '' && <option value={cur}>{`不明 (ID:${cur})`}</option>}
        {opts.map(([id, name]) => <option key={id} value={String(id)}>{`${name} (${id})`}</option>)}
      </select>
    );
  }
  if (field.kind === 'string') {
    const kl = field.key.toLowerCase();
    // vendor_default 等: 部門「名」文字列 → canonical 部門名ピッカー (自由入力による非実在部門名の混入を防ぐ)
    if ((kl === 'section' || kl.endsWith('section_name')) && maps.sections.size > 0) {
      const names = Array.from(new Set(Array.from(maps.sections.values()))).sort((a, b) => a.localeCompare(b, 'ja'));
      const known = names.includes(field.value);
      return (
        <select style={S.formInput} value={field.value} onChange={(e) => onChange(e.target.value)}>
          {field.value === '' && <option value="">(未設定)</option>}
          {!known && field.value !== '' && <option value={field.value}>{`${field.value} (canonical外)`}</option>}
          {names.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      );
    }
    // knowledge 等の長文 (note/text/memo) は textarea で編集
    if (kl === 'note' || kl === 'text' || kl === 'memo' || kl === 'knowledge' || field.value.length > 60) {
      return (
        <textarea
          style={{ ...S.formInput, height: 72, padding: '6px 8px', resize: 'vertical', lineHeight: 1.5 }}
          value={field.value}
          placeholder={field.wasNull ? '(null)' : ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    }
  }
  if (field.kind === 'boolean') {
    return (
      <select style={S.formInput} value={field.value} onChange={(e) => onChange(e.target.value)}>
        <option value="true">true</option>
        <option value="false">false</option>
      </select>
    );
  }
  return (
    <input
      type={field.kind === 'number' ? 'number' : 'text'}
      style={{ ...S.formInput, ...(field.kind === 'json' ? { fontFamily: NUM } : {}) }}
      value={field.value}
      placeholder={field.kind === 'json' ? 'JSON (配列/オブジェクト)' : field.wasNull ? '(null)' : ''}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

// key-value 閲覧テーブル
const KVTable: React.FC<{ obj: Record<string, any> | null; maps: NameMaps3; emptyText: string }> = ({ obj, maps, emptyText }) => {
  if (!obj || Object.keys(obj).length === 0) {
    return <div style={{ padding: '10px 12px', color: C.sub, fontSize: 12 }}>{emptyText}</div>;
  }
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <tbody>
        {Object.entries(obj).map(([k, v], i, arr) => (
          <tr key={k}>
            <td style={{ ...S.kvKey, ...(i === arr.length - 1 ? { borderBottom: 0 } : {}) }}>{k}</td>
            <td style={{ ...S.kvVal, ...(i === arr.length - 1 ? { borderBottom: 0 } : {}) }} title={scalarStr(v)}>
              {trunc(resolveIdValue(k, v, maps), 300)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// dry-run preview 等の未知形状配列を汎用表で描画
const GenericTable: React.FC<{ rows: any[] }> = ({ rows }) => {
  const cols = useMemo(() => {
    const keys: string[] = [];
    for (const r of rows.slice(0, 30)) {
      if (r && typeof r === 'object' && !Array.isArray(r)) {
        for (const k of Object.keys(r)) if (!keys.includes(k)) keys.push(k);
      }
    }
    return keys.slice(0, 10);
  }, [rows]);
  if (rows.length === 0) return <div style={{ padding: '10px 12px', color: C.sub, fontSize: 12 }}>0件</div>;
  if (cols.length === 0) {
    return <div style={S.rawPre as React.CSSProperties}>{rows.map((r, i) => <div key={i}>{scalarStr(r)}</div>)}</div>;
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
        <thead><tr>{cols.map((c) => <th key={c} style={S.th}>{c}</th>)}</tr></thead>
        <tbody>
          {rows.slice(0, 50).map((r, i) => (
            <tr key={i}>
              {cols.map((c) => (
                <td key={c} style={{ ...S.td, fontFamily: NUM, fontSize: 11 }} title={scalarStr(r?.[c])}>
                  {trunc(scalarStr(r?.[c]), 60)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > 50 && <div style={{ padding: '6px 10px', color: C.sub, fontSize: 11 }}>ほか {rows.length - 50} 件 (先頭50件のみ表示)</div>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// 新規ルール作成フォーム (POST /api/rulebook — origin='manual_ui'・pinned=1)
//   domain 別の専用フォーム: 値は canonical 候補 (部門/勘定はマスタ) からのピッカーのみ。
//   match_keys/payload の形は worker 側 read-through 写像 (rulebookMapSourceRow /
//   ops_flags vendor_default 写像) と同形に揃える。
// ---------------------------------------------------------------------------
const CREATE_DOMAINS: Array<{ key: string; label: string; desc: string }> = [
  { key: 'vendor_default', label: '業者既定部門', desc: '業者名 (部分一致キー) → 固定部門。請求書に部門が印字されない業者向け (例: 株式会社貴千 → 山城屋庄藏)' },
  { key: 'curated_default', label: '業者既定勘定', desc: '取引先 → 既定の勘定科目 (+任意で部門・税区分)。proposal 生成時の初期値になる' },
  { key: 'section_alias', label: '宛名→部門対応', desc: '請求書の宛名・届け先の表記 → 部門の対応表 (宛名から部門を解決)' },
  { key: 'knowledge', label: '自由知識', desc: '機械は自動適用しない参考知識。人と AI が判断時の文脈として参照するメモ' },
];

const formRowSt: React.CSSProperties = { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 };
const formLabelSt: React.CSSProperties = { flex: '0 0 150px', color: C.sub, fontSize: 11, fontWeight: 800 };

const CreateRuleForm: React.FC<{
  maps: NameMaps3;
  onCancel: () => void;
  onCreated: (ruleId: string | null) => void;
}> = ({ maps, onCancel, onCreated }) => {
  const [cDomain, setCDomain] = useState('vendor_default');
  const [partnerText, setPartnerText] = useState('');   // vendor_default: 業者名 / curated_default: 取引先名 / knowledge: 対象トピック
  const [sectionName, setSectionName] = useState('');   // vendor_default: canonical 部門名
  const [sectionId, setSectionId] = useState('');       // curated_default(任意) / section_alias(必須)
  const [accountId, setAccountId] = useState('');       // curated_default: 既定勘定科目
  const [taxCode, setTaxCode] = useState('');           // curated_default: 既定税区分 (任意)
  const [aliasText, setAliasText] = useState('');       // section_alias: 宛名表記
  const [noteText, setNoteText] = useState('');         // knowledge: 本文
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [partners, setPartners] = useState<Map<number, string> | null>(null);

  // 取引先マスタは curated_default 選択時のみ遅延ロード (module cache 済)
  useEffect(() => {
    if (cDomain !== 'curated_default' || partners) return;
    fetchPartnersMap().then(setPartners).catch(() => setPartners(new Map()));
  }, [cDomain, partners]);

  const partnerIdByName = useMemo(() => {
    const m = new Map<string, number>();
    if (partners) for (const [id, name] of Array.from(partners.entries())) { if (!m.has(name)) m.set(name, id); }
    return m;
  }, [partners]);
  const resolvedPartnerId = partnerIdByName.get(partnerText.trim()) ?? null;

  const sectionOpts = useMemo(
    () => Array.from(maps.sections.entries()).sort((a, b) => a[1].localeCompare(b[1], 'ja')), [maps.sections]);
  const accountOpts = useMemo(
    () => Array.from(maps.accounts.entries()).sort((a, b) => a[1].localeCompare(b[1], 'ja')), [maps.accounts]);
  const taxOpts = useMemo(
    () => Array.from(maps.taxes.entries()).sort((a, b) => a[1].localeCompare(b[1], 'ja')), [maps.taxes]);
  const sectionNames = useMemo(
    () => Array.from(new Set(Array.from(maps.sections.values()))).sort((a, b) => a.localeCompare(b, 'ja')), [maps.sections]);

  const submit = useCallback(async () => {
    if (saving) return;
    setError(null);
    let match_keys: Record<string, any>; let payload: Record<string, any>; let provenance: Record<string, any>;
    const note = reason.trim() || undefined;
    if (cDomain === 'vendor_default') {
      if (!partnerText.trim()) { setError('業者名 (部分一致キー) を入力してください'); return; }
      if (!sectionName) { setError('固定する部門を選択してください'); return; }
      // worker の ops_flags 'invoice_vendor_default_sections' 写像と同形 (payload.section = canonical 部門名)
      match_keys = { vendor_name_key: partnerText.trim(), match: 'normalized_partial' };
      payload = { section: sectionName };
      provenance = { vendor_name: partnerText.trim(), section_name: sectionName, created_from: 'rulebook_ui', note };
    } else if (cDomain === 'curated_default') {
      if (resolvedPartnerId === null) { setError('取引先を候補から選択してください (取引先マスタに完全一致する名称が必要です)'); return; }
      if (!accountId) { setError('既定の勘定科目を選択してください'); return; }
      // partner_curated_patterns 写像と同形 (match_keys.partner_id / payload は ID 群)
      match_keys = { partner_id: resolvedPartnerId };
      payload = {
        account_item_id: Number(accountId),
        section_id: sectionId ? Number(sectionId) : null,
        tax_code: taxCode ? Number(taxCode) : null,
      };
      provenance = {
        partner_name: partnerText.trim(),
        account_item_name: maps.accounts.get(Number(accountId)) || null,
        section_name: sectionId ? (maps.sections.get(Number(sectionId)) || null) : null,
        created_from: 'rulebook_ui', note,
      };
    } else if (cDomain === 'section_alias') {
      if (!aliasText.trim()) { setError('宛名・届け先の表記を入力してください'); return; }
      if (!sectionId) { setError('対応する部門を選択してください'); return; }
      match_keys = { alias: aliasText.trim() };
      payload = { section_id: Number(sectionId) };
      provenance = { alias: aliasText.trim(), section_name: maps.sections.get(Number(sectionId)) || null, created_from: 'rulebook_ui', note };
    } else { // knowledge
      if (!partnerText.trim()) { setError('対象 (業者名・テーマ) を入力してください'); return; }
      if (!noteText.trim()) { setError('知識の本文を入力してください'); return; }
      match_keys = { topic: partnerText.trim() };
      payload = { note: noteText.trim(), auto_apply: false }; // 機械は自動適用しない参考知識
      provenance = { created_from: 'rulebook_ui', note };
    }
    setSaving(true);
    try {
      const r = await createRulebookRule({
        domain: cDomain, match_keys, payload, provenance,
        reason: reason.trim() || `manual create (${cDomain})`,
      });
      onCreated((r && r.rule && r.rule.rule_id) || null);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(`作成失敗: ${msg}${/invalid_domain/.test(msg) ? ' — worker 側がこの domain に未対応の可能性があります (デプロイ待ち)' : ''}`);
      setSaving(false);
    }
  }, [saving, cDomain, partnerText, sectionName, sectionId, accountId, taxCode, aliasText, noteText, reason, resolvedPartnerId, maps, onCreated]);

  const domainMeta = CREATE_DOMAINS.find((d) => d.key === cDomain)!;

  return (
    <div style={{ ...S.box, padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, color: '#344b62', fontSize: 12.5, fontWeight: 800 }}>
        <MdAdd size={15} /> 新規ルール作成 (origin=manual_ui・pinned で保存)
      </div>
      {/* domain 選択 */}
      <div style={{ ...S.tabControl, display: 'inline-flex', margin: '6px 0 4px' }}>
        {CREATE_DOMAINS.map((d, i) => (
          <button key={d.key} type="button"
            style={{ ...tabBtnSt(cDomain === d.key), ...(i === 0 ? { borderLeft: 0 } : {}) }}
            onClick={() => { setCDomain(d.key); setError(null); }} aria-pressed={cDomain === d.key}>
            {d.label}
          </button>
        ))}
      </div>
      <div style={{ color: C.sub, fontSize: 11, marginBottom: 12 }}>{domainMeta.desc}</div>

      {cDomain === 'vendor_default' && (
        <>
          <div style={formRowSt}>
            <span style={formLabelSt}>業者名 (部分一致キー)</span>
            <input type="text" style={{ ...S.formInput, width: 320 }} value={partnerText}
              placeholder="例: 株式会社貴千" onChange={(e) => setPartnerText(e.target.value)} />
          </div>
          <div style={formRowSt}>
            <span style={formLabelSt}>固定する部門</span>
            <select style={{ ...S.formInput, width: 320 }} value={sectionName} onChange={(e) => setSectionName(e.target.value)}>
              <option value="">(部門を選択)</option>
              {sectionNames.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </>
      )}

      {cDomain === 'curated_default' && (
        <>
          <div style={formRowSt}>
            <span style={formLabelSt}>取引先</span>
            <input type="text" style={{ ...S.formInput, width: 320 }} value={partnerText} list="rb-create-partners"
              placeholder={partners === null ? '取引先マスタを読込中…' : '取引先名 (候補から選択)'}
              onChange={(e) => setPartnerText(e.target.value)} />
            <datalist id="rb-create-partners">
              {partners && Array.from(partners.entries()).map(([id, name]) => <option key={id} value={name} />)}
            </datalist>
            {partnerText.trim() !== '' && (
              resolvedPartnerId !== null
                ? <span style={S.chip}>partner_id: {resolvedPartnerId}</span>
                : <span style={{ color: C.warn, fontSize: 11, fontWeight: 700 }}>マスタに完全一致なし</span>
            )}
          </div>
          <div style={formRowSt}>
            <span style={formLabelSt}>既定の勘定科目</span>
            <select style={{ ...S.formInput, width: 320 }} value={accountId} onChange={(e) => setAccountId(e.target.value)}>
              <option value="">(勘定科目を選択)</option>
              {accountOpts.map(([id, name]) => <option key={id} value={String(id)}>{`${name} (${id})`}</option>)}
            </select>
          </div>
          <div style={formRowSt}>
            <span style={formLabelSt}>既定の部門 (任意)</span>
            <select style={{ ...S.formInput, width: 320 }} value={sectionId} onChange={(e) => setSectionId(e.target.value)}>
              <option value="">(未設定)</option>
              {sectionOpts.map(([id, name]) => <option key={id} value={String(id)}>{`${name} (${id})`}</option>)}
            </select>
          </div>
          <div style={formRowSt}>
            <span style={formLabelSt}>既定の税区分 (任意)</span>
            <select style={{ ...S.formInput, width: 320 }} value={taxCode} onChange={(e) => setTaxCode(e.target.value)}>
              <option value="">(未設定)</option>
              {taxOpts.map(([id, name]) => <option key={id} value={String(id)}>{`${name} (${id})`}</option>)}
            </select>
          </div>
        </>
      )}

      {cDomain === 'section_alias' && (
        <>
          <div style={formRowSt}>
            <span style={formLabelSt}>宛名・届け先の表記</span>
            <input type="text" style={{ ...S.formInput, width: 320 }} value={aliasText}
              placeholder="例: アリカ立川店 御中" onChange={(e) => setAliasText(e.target.value)} />
          </div>
          <div style={formRowSt}>
            <span style={formLabelSt}>対応する部門</span>
            <select style={{ ...S.formInput, width: 320 }} value={sectionId} onChange={(e) => setSectionId(e.target.value)}>
              <option value="">(部門を選択)</option>
              {sectionOpts.map(([id, name]) => <option key={id} value={String(id)}>{`${name} (${id})`}</option>)}
            </select>
          </div>
        </>
      )}

      {cDomain === 'knowledge' && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', marginBottom: 10, border: `1px solid ${C.blueLine}`, borderRadius: 5, background: C.blueTint, color: '#33699e', fontSize: 11.5, fontWeight: 700 }}>
            <MdInfoOutline size={14} /> 機械は自動適用しない参考知識として保存されます (auto_apply=false)
          </div>
          <div style={formRowSt}>
            <span style={formLabelSt}>対象 (業者名・テーマ)</span>
            <input type="text" style={{ ...S.formInput, width: 320 }} value={partnerText}
              placeholder="例: プライムミート / 25日払い全般" onChange={(e) => setPartnerText(e.target.value)} />
          </div>
          <div style={{ ...formRowSt, alignItems: 'flex-start' }}>
            <span style={{ ...formLabelSt, paddingTop: 6 }}>知識の本文</span>
            <textarea style={{ ...S.formInput, width: 480, maxWidth: '100%', height: 88, padding: '6px 8px', resize: 'vertical', lineHeight: 1.5 }}
              value={noteText}
              placeholder="例: この業者の請求書は前月分が翌月5日に届く。金額欄は税込のみ印字"
              onChange={(e) => setNoteText(e.target.value)} />
          </div>
        </>
      )}

      <div style={{ ...formRowSt, marginTop: 10, marginBottom: 0 }}>
        <span style={formLabelSt}>備考・理由 (任意)</span>
        <input type="text" style={{ ...S.formInput, width: 320 }} value={reason}
          placeholder="例: 7月請求書レビューで確認" onChange={(e) => setReason(e.target.value)} />
        <Button appearance="primary" small disabled={saving} onClick={submit}>
          {saving ? <InlineSpinner isLoading /> : <MdAdd />} 作成
        </Button>
        <button type="button" style={S.smallBtn} onClick={onCancel}>キャンセル</button>
      </div>
      {error && <div style={{ marginTop: 8, color: C.danger, fontSize: 12, fontWeight: 700 }}>{error}</div>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// hash deep-link: #rulebook/<rule_id>
// ---------------------------------------------------------------------------
const readHashId = (): string | null => {
  const m = window.location.hash.match(/^#?rulebook\/(.+)$/);
  return m ? decodeURIComponent(m[1]) : null;
};

interface EditState {
  matchFields: EditField[]; payloadFields: EditField[]; reason: string;
  saving: boolean; error: string | null; conflict: boolean;
}

// ---------------------------------------------------------------------------
// メインページ
// ---------------------------------------------------------------------------
export const RulebookPage: React.FC = () => {
  const [rules, setRules] = useState<RulebookRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [domain, setDomain] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [qInput, setQInput] = useState('');
  const [appliedQ, setAppliedQ] = useState('');

  const [statsByDomain, setStatsByDomain] = useState<Record<string, number> | null>(null);
  const [statsTotal, setStatsTotal] = useState<number | null>(null);

  const [maps, setMaps] = useState<NameMaps3>(EMPTY_MAPS);
  const [mapsNote, setMapsNote] = useState<string | null>(null);

  const [syncing, setSyncing] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [toast, setToast] = useState<{ msg: string; kind: 'good' | 'danger' } | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [expandedId, setExpandedId] = useState<string | null>(() => readHashId());
  const [detail, setDetail] = useState<RulebookRuleDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const [edit, setEdit] = useState<EditState | null>(null);
  const [fbComment, setFbComment] = useState('');
  const [fbSending, setFbSending] = useState(false);
  const [armDisable, setArmDisable] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [dryRun, setDryRun] = useState<{ running: boolean; result: RulebookDryRunResult | null; error: string | null }>({ running: false, result: null, error: null });
  const [showRaw, setShowRaw] = useState(false);

  const showToast = useCallback((msg: string, kind: 'good' | 'danger' = 'good') => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ msg, kind });
    toastTimer.current = setTimeout(() => setToast(null), 6000);
  }, []);
  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  // マスタ名称 (module cache 済 = 初回のみ実 fetch)。失敗しても ID 表示で継続
  useEffect(() => {
    (async () => {
      const [a, s, t] = await Promise.allSettled([fetchAccountItemsMap(), fetchSectionsMap(), fetchTaxCodesMap()]);
      setMaps({
        accounts: a.status === 'fulfilled' ? a.value : new Map(),
        sections: s.status === 'fulfilled' ? s.value : new Map(),
        taxes: t.status === 'fulfilled' ? t.value : new Map(),
      });
      if (a.status === 'rejected' || s.status === 'rejected' || t.status === 'rejected') {
        setMapsNote('マスタ名称の取得に失敗したため、勘定・部門・税は ID のみで表示しています');
      }
    })();
  }, []);

  // stats (domain別件数)。失敗は無視 (一覧から代替算出)
  const loadStats = useCallback(async () => {
    try {
      const r: RulebookStatsResponse = await fetchRulebookStats();
      const byDomain: Record<string, number> = {};
      const src = (r.by_domain && typeof r.by_domain === 'object') ? r.by_domain
        : (r as any).byDomain && typeof (r as any).byDomain === 'object' ? (r as any).byDomain : null;
      if (src) {
        for (const [k, v] of Object.entries(src)) byDomain[k] = Number(v) || 0;
      } else if (Array.isArray(r.domains)) {
        for (const d of r.domains) if (d && d.domain) byDomain[String(d.domain)] = Number(d.count ?? d.total ?? 0);
      } else if (Array.isArray((r as any).stats)) {
        for (const d of (r as any).stats) if (d && d.domain) byDomain[String(d.domain)] = Number(d.count ?? d.total ?? 0);
      }
      if (Object.keys(byDomain).length > 0) {
        setStatsByDomain(byDomain);
        setStatsTotal(typeof r.total === 'number' ? r.total : Object.values(byDomain).reduce((x, y) => x + y, 0));
      } else if (typeof r.total === 'number') {
        setStatsTotal(r.total);
      }
    } catch { /* stats 未デプロイでも一覧は動かす */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setListError(null);
    try {
      const r = await fetchRulebookRules({
        domain: domain || undefined,
        status: statusFilter || undefined,
        q: appliedQ || undefined,
      });
      const list = Array.isArray(r.rules) ? r.rules : [];
      list.sort((a, b) => {
        if ((b.pinned ? 1 : 0) !== (a.pinned ? 1 : 0)) return (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0);
        const av = Number(a.updated_at || a.created_at || 0); const bv = Number(b.updated_at || b.created_at || 0);
        return bv - av;
      });
      setRules(list);
      // stats 未取得時は全件ロード結果から domain 件数を代替算出
      if (!statsByDomain && !domain && !statusFilter && !appliedQ) {
        const byDomain: Record<string, number> = {};
        for (const it of list) byDomain[it.domain] = (byDomain[it.domain] || 0) + 1;
        setStatsByDomain(byDomain);
        setStatsTotal(list.length);
      }
    } catch (e: unknown) {
      setRules([]);
      setListError(`ルール取得失敗: ${e instanceof Error ? e.message : String(e)} — worker API (/api/rulebook) が未デプロイの可能性があります`);
    } finally {
      setLoading(false);
    }
  }, [domain, statusFilter, appliedQ, statsByDomain]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStats(); }, [loadStats]);

  // hash 連動 (#rulebook/<id>)
  useEffect(() => {
    const onHash = () => setExpandedId(readHashId());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const toggleRow = useCallback((id: string) => {
    const next = expandedId === id ? null : id;
    window.location.hash = next ? `#rulebook/${encodeURIComponent(next)}` : '#rulebook';
    setExpandedId(next);
  }, [expandedId]);

  // 詳細ロード (展開時)。展開切替で編集・dry-run 等の子状態をリセット
  const loadDetail = useCallback(async (id: string) => {
    setDetailLoading(true); setDetailError(null); setDetail(null);
    try {
      const d = await fetchRulebookRuleDetail(id);
      setDetail(d);
    } catch (e: unknown) {
      setDetailError(`詳細取得失敗: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setDetailLoading(false);
    }
  }, []);
  useEffect(() => {
    setEdit(null); setFbComment(''); setArmDisable(false); setShowRaw(false);
    setDryRun({ running: false, result: null, error: null });
    if (expandedId) loadDetail(expandedId);
    else { setDetail(null); setDetailError(null); }
  }, [expandedId, loadDetail]);

  // freee学習を同期
  const runSync = useCallback(async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const r = await syncRulebookLearning();
      let cnt: string | null = null;
      for (const k of ['synced', 'imported', 'upserted', 'inserted', 'updated', 'count', 'total']) {
        if (typeof (r as any)[k] === 'number') { cnt = `${(r as any)[k].toLocaleString('ja-JP')}件`; break; }
      }
      if (!cnt && (r as any).counts && typeof (r as any).counts === 'object') {
        cnt = Object.entries((r as any).counts).map(([k, v]) => `${k} ${v}`).join(' / ');
      }
      showToast(`freeeの学習を同期しました${cnt ? ` (${cnt})` : ''}`, 'good');
      await Promise.all([load(), loadStats()]);
    } catch (e: unknown) {
      showToast(`同期失敗: ${e instanceof Error ? e.message : String(e)}`, 'danger');
    } finally {
      setSyncing(false);
    }
  }, [syncing, load, loadStats, showToast]);

  // 新規作成完了 → 一覧/統計を再取得し、作成ルールを展開表示
  const handleCreated = useCallback(async (ruleId: string | null) => {
    setShowCreate(false);
    showToast(ruleId ? `ルール ${ruleId} を作成しました (origin=manual_ui・pinned)` : 'ルールを作成しました', 'good');
    await Promise.all([load(), loadStats()]);
    if (ruleId) {
      window.location.hash = `#rulebook/${encodeURIComponent(ruleId)}`;
      setExpandedId(ruleId);
    }
  }, [load, loadStats, showToast]);

  // 編集
  const beginEdit = useCallback(() => {
    if (!detail) return;
    setEdit({
      matchFields: toFields(parseJsonSafe(detail.rule.match_keys_json) || {}),
      payloadFields: toFields(parseJsonSafe(detail.rule.payload_json) || {}),
      reason: '', saving: false, error: null, conflict: false,
    });
  }, [detail]);

  const saveEdit = useCallback(async () => {
    if (!detail || !edit || edit.saving) return;
    const mk = fromFields(edit.matchFields);
    if (!mk.ok) { setEdit({ ...edit, error: `対象条件: ${mk.error}` }); return; }
    const pl = fromFields(edit.payloadFields);
    if (!pl.ok) { setEdit({ ...edit, error: `帰結: ${pl.error}` }); return; }
    if (!edit.reason.trim()) { setEdit({ ...edit, error: '変更理由 (reason) は必須です' }); return; }
    setEdit({ ...edit, saving: true, error: null, conflict: false });
    try {
      await patchRulebookRule(detail.rule.rule_id, {
        expected_version: Number(detail.rule.version),
        match_keys: mk.obj,
        payload: pl.obj,
        reason: edit.reason.trim(),
      });
      setEdit(null);
      showToast(`ルール ${detail.rule.rule_id} を更新しました (新version作成・pinned保護)`, 'good');
      await Promise.all([loadDetail(detail.rule.rule_id), load()]);
    } catch (e: unknown) {
      const st = (e as any)?.status;
      if (st === 409) {
        setEdit((prev) => prev && ({ ...prev, saving: false, conflict: true, error: '他の変更と競合しました (409)。「最新を再読込」してから編集し直してください' }));
      } else {
        setEdit((prev) => prev && ({ ...prev, saving: false, error: `保存失敗: ${e instanceof Error ? e.message : String(e)}` }));
      }
    }
  }, [detail, edit, loadDetail, load, showToast]);

  const reloadAfterConflict = useCallback(async () => {
    if (!expandedId) return;
    setEdit(null);
    await loadDetail(expandedId);
  }, [expandedId, loadDetail]);

  // feedback
  const sendFeedback = useCallback(async (verdict: 'correct' | 'wrong') => {
    if (!detail || fbSending) return;
    setFbSending(true);
    try {
      await postRulebookFeedback(detail.rule.rule_id, { verdict, comment: fbComment.trim() || undefined });
      showToast(`フィードバックを記録しました (${VERDICT_META[verdict].label})`, 'good');
      setFbComment('');
      await loadDetail(detail.rule.rule_id);
      await load(); // 一覧行の成績カウンタも即時反映 (2026-07-07 UIデバッグ)
    } catch (e: unknown) {
      showToast(`フィードバック失敗: ${e instanceof Error ? e.message : String(e)}`, 'danger');
    } finally {
      setFbSending(false);
    }
  }, [detail, fbSending, fbComment, loadDetail, load, showToast]);

  // 無効化 / 再有効化 (無効化のみ二段階確認)
  const doDisable = useCallback(async () => {
    if (!detail || actionBusy) return;
    setActionBusy(true);
    try {
      await disableRulebookRule(detail.rule.rule_id);
      showToast(`ルール ${detail.rule.rule_id} を無効化しました`, 'good');
      setArmDisable(false);
      await Promise.all([loadDetail(detail.rule.rule_id), load()]);
    } catch (e: unknown) {
      showToast(`無効化失敗: ${e instanceof Error ? e.message : String(e)}`, 'danger');
    } finally {
      setActionBusy(false);
    }
  }, [detail, actionBusy, loadDetail, load, showToast]);

  const doEnable = useCallback(async () => {
    if (!detail || actionBusy) return;
    setActionBusy(true);
    try {
      await enableRulebookRule(detail.rule.rule_id);
      showToast(`ルール ${detail.rule.rule_id} を再有効化しました`, 'good');
      await Promise.all([loadDetail(detail.rule.rule_id), load()]);
    } catch (e: unknown) {
      showToast(`再有効化失敗: ${e instanceof Error ? e.message : String(e)}`, 'danger');
    } finally {
      setActionBusy(false);
    }
  }, [detail, actionBusy, loadDetail, load, showToast]);

  // dry-run
  const runDryRun = useCallback(async () => {
    if (!detail || dryRun.running) return;
    setDryRun({ running: true, result: null, error: null });
    try {
      const r = await dryRunRulebookRule(detail.rule.rule_id, {});
      setDryRun({ running: false, result: r, error: null });
    } catch (e: unknown) {
      setDryRun({ running: false, result: null, error: e instanceof Error ? e.message : String(e) });
    }
  }, [detail, dryRun.running]);

  // 一覧の行ビューモデル
  const rows = useMemo(() => rules.map((r) => {
    const mk = parseJsonSafe(r.match_keys_json);
    const pl = parseJsonSafe(r.payload_json);
    const matchText = mk && typeof mk === 'object'
      ? Object.entries(mk).map(([k, v]) => `${k}=${scalarStr(v)}`).join(' / ')
      : (r.match_keys_json || '—');
    const chips: string[] = [];
    if (pl && typeof pl === 'object') {
      for (const [k, v] of Object.entries(pl)) {
        const kl = k.toLowerCase();
        if (kl.endsWith('account_item_id')) chips.push(`勘定 ${resolveIdValue(k, v, maps)}`);
        else if (kl.endsWith('section_id')) chips.push(`部門 ${resolveIdValue(k, v, maps)}`);
        else if (kl.endsWith('tax_code')) chips.push(`税 ${resolveIdValue(k, v, maps)}`);
      }
      for (const [k, v] of Object.entries(pl)) {
        if (chips.length >= 5) break;
        const kl = k.toLowerCase();
        if (kl.endsWith('account_item_id') || kl.endsWith('section_id') || kl.endsWith('tax_code')) continue;
        chips.push(`${k}: ${trunc(scalarStr(v), 24)}`);
      }
    }
    return { r, matchText, chips, attention: isAttention(r) };
  }), [rules, maps]);

  const MAX_ROWS = 300;

  const statusBadge = (st: string) => {
    const m = STATUS_META[st] || { label: st, bg: '#f1f4f7', fg: '#5e7083', bd: '#ccd6df' };
    return <span style={S.badge(m.bg, m.fg, m.bd)}>{m.label}</span>;
  };

  // 由来タイムライン用: provenance を key-value に展開
  const provenanceObj = detail ? parseJsonSafe(detail.rule.provenance_json) : null;

  return (
    <div style={S.page}>
      <Container>
        <section style={S.sheet}>
          {/* navy ヘッダ */}
          <header style={S.top}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', minWidth: 0 }}>
              <h1 style={S.topTitle}>ルールブック</h1>
              <span style={S.topCaption}>学習された仕訳ルールの閲覧・編集・無効化・テスト実行 (学習の一級市民化)</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {statsTotal !== null && <span style={S.headerChip}>全 {statsTotal.toLocaleString('ja-JP')} 件</span>}
              {statsByDomain && Object.entries(statsByDomain)
                .sort((a, b) => b[1] - a[1]).slice(0, 8)
                .map(([d, n]) => (
                  <span key={d} style={S.headerChip}>{DOMAIN_LABEL[d] || d} {n.toLocaleString('ja-JP')}</span>
                ))}
              <Button appearance="primary" small onClick={runSync} disabled={syncing}>
                {syncing ? <InlineSpinner isLoading /> : <MdSync />} freeeの学習を同期
              </Button>
            </div>
          </header>

          {listError && <div style={{ padding: 12 }}><MessageBlock error>{listError}</MessageBlock></div>}
          {mapsNote && <div style={{ padding: '8px 20px', color: C.warn, fontSize: 11, fontWeight: 700, background: C.warnBg, borderBottom: `1px solid ${C.line}` }}>{mapsNote}</div>}

          {/* フィルタ行: domainタブ + 検索 + statusフィルタ */}
          <section style={S.filterRow}>
            <div style={S.tabControl}>
              {DOMAIN_TABS.map((t, i) => (
                <button key={t.key} type="button"
                  style={{ ...tabBtnSt(domain === t.key), ...(i === 0 ? { borderLeft: 0 } : {}) }}
                  onClick={() => setDomain(t.key)} aria-pressed={domain === t.key}>
                  {t.label}
                </button>
              ))}
            </div>
            <select style={S.filterInput} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">全状態</option>
              <option value="active">有効 (active)</option>
              <option value="staging">候補 (staging)</option>
              <option value="disabled">無効 (disabled)</option>
              <option value="superseded">置換済 (superseded)</option>
            </select>
            <input type="text" style={{ ...S.filterInput, width: 240 }}
              placeholder="検索 (業者名・摘要・キー / Enterで実行)"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') setAppliedQ(qInput.trim()); }} />
            <button type="button" style={S.smallBtn} onClick={() => setAppliedQ(qInput.trim())}>検索</button>
            {appliedQ && (
              <button type="button" style={S.smallBtn} onClick={() => { setQInput(''); setAppliedQ(''); }}>検索解除</button>
            )}
            <span style={{ flex: 1 }} />
            <span style={S.filterLabel}>{rules.length.toLocaleString('ja-JP')} 件</span>
            <Button appearance="primary" small onClick={() => setShowCreate((v) => !v)}>
              <MdAdd /> {showCreate ? '作成を閉じる' : '新規ルール'}
            </Button>
            <Button appearance="secondary" small onClick={load} disabled={loading}>
              {loading ? <InlineSpinner isLoading /> : <MdRefresh />} 更新
            </Button>
          </section>

          {/* 新規ルール作成パネル */}
          {showCreate && (
            <section style={{ padding: '12px 18px', borderBottom: `1px solid ${C.line}`, background: '#fbfcfd' }}>
              <CreateRuleForm maps={maps} onCancel={() => setShowCreate(false)} onCreated={handleCreated} />
            </section>
          )}

          {/* 一覧 */}
          <section style={S.list}>
            {loading && (
              <div style={{ padding: 30, textAlign: 'center', color: C.sub }}><InlineSpinner isLoading /> ルールを取得中…</div>
            )}
            {!loading && rules.length === 0 && !listError && (
              <div style={{ padding: 30, textAlign: 'center', color: C.sub, fontWeight: 700 }}>
                条件に一致するルールがありません。「freeeの学習を同期」で既存学習を取り込めます。
              </div>
            )}
            {!loading && rules.length > 0 && (
              <div style={S.tableWrap}>
                <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
                  <thead>
                    <tr>
                      <th style={S.th}>状態</th>
                      <th style={S.th}>ドメイン</th>
                      <th style={S.th}>対象 (match_keys)</th>
                      <th style={S.th}>帰結 (payload)</th>
                      <th style={S.th}>成績</th>
                      <th style={S.th}>信頼度</th>
                      <th style={S.th}>更新</th>
                      <th style={{ ...S.th, width: 26 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, MAX_ROWS).map(({ r, matchText, chips, attention }) => {
                      const isOpen = expandedId === r.rule_id;
                      return (
                        <React.Fragment key={r.rule_id}>
                          <tr className="rb-row" style={{ cursor: 'pointer', background: isOpen ? '#f8fafc' : undefined }}
                            onClick={() => toggleRow(r.rule_id)}>
                            <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                              {statusBadge(r.status)}
                              {!!r.pinned && <span title="pinned (rebuild絶対不可侵)" style={{ marginLeft: 4, color: '#33699e', verticalAlign: 'middle' }}><MdPushPin size={13} /></span>}
                              {attention && <span style={{ ...S.badge(C.warnBg, C.warn, '#e3c68a'), marginLeft: 4 }}><MdWarningAmber size={11} /> 差戻し多</span>}
                            </td>
                            <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                              <span style={S.chip}>{DOMAIN_LABEL[r.domain] || r.domain}</span>
                              {r.domain === 'knowledge' && (
                                <span style={S.chip} title={KNOWLEDGE_NOTE}>自動適用なし</span>
                              )}
                            </td>
                            <td style={{ ...S.td, maxWidth: 340 }} title={matchText}>
                              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 340, fontFamily: NUM, fontSize: 11.5 }}>{matchText}</div>
                              <div style={{ marginTop: 2, color: C.sub, fontSize: 10, fontFamily: NUM }}>{r.rule_id}</div>
                            </td>
                            <td style={{ ...S.td, maxWidth: 360 }}>
                              {chips.length > 0
                                ? chips.map((c, i) => <span key={i} style={S.chip}>{c}</span>)
                                : <span style={{ color: C.sub }}>—</span>}
                            </td>
                            <td style={{ ...S.td, whiteSpace: 'nowrap', fontFamily: NUM, fontSize: 11.5 }}>
                              {Number(r.sample_count || 0)}件 ・ <span style={{ color: C.good }}>{Number(r.approve_count || 0)}承認</span>
                              {' / '}<span style={{ color: Number(r.reject_count || 0) > 0 ? C.danger : C.sub }}>{Number(r.reject_count || 0)}差戻</span>
                            </td>
                            <td style={{ ...S.td, whiteSpace: 'nowrap', fontFamily: NUM, fontSize: 11.5 }}>
                              {r.confidence === null || r.confidence === undefined ? '—'
                                : Number(r.confidence) <= 1 ? `${Math.round(Number(r.confidence) * 100)}%` : String(r.confidence)}
                            </td>
                            <td style={{ ...S.td, whiteSpace: 'nowrap', fontFamily: NUM, fontSize: 11 }}>{fmtTs(r.updated_at || r.created_at)}</td>
                            <td style={{ ...S.td, color: '#698096' }}>
                              <span style={{ display: 'inline-block', transition: 'transform .16s ease', transform: isOpen ? 'rotate(180deg)' : undefined }}>
                                <MdExpandMore size={16} />
                              </span>
                            </td>
                          </tr>

                          {isOpen && (
                            <tr>
                              <td colSpan={8} style={{ padding: 0, borderBottom: `1px solid ${C.lineDark}` }}>
                                <div style={S.detailWrap}>
                                  {detailLoading && <div style={{ padding: 20, textAlign: 'center', color: C.sub }}><InlineSpinner isLoading /> 詳細を取得中…</div>}
                                  {detailError && <MessageBlock error>{detailError}</MessageBlock>}
                                  {detail && detail.rule.rule_id === r.rule_id && (
                                    <>
                                      {/* アクションバー */}
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                        <b style={{ fontFamily: NUM, fontSize: 13 }}>{detail.rule.rule_id}</b>
                                        {statusBadge(detail.rule.status)}
                                        <span style={S.chip}>v{detail.rule.version}</span>
                                        <span style={S.chip}>{ORIGIN_LABEL[detail.rule.origin || ''] || detail.rule.origin || '—'}</span>
                                        {!!detail.rule.pinned && <span style={S.badge(C.blueTint, '#33699e', C.blueLine)}><MdPushPin size={11} /> pinned</span>}
                                        <span style={{ flex: 1 }} />
                                        <button type="button" style={S.smallBtn} disabled={dryRun.running}
                                          onClick={(e) => { e.stopPropagation(); runDryRun(); }}>
                                          {dryRun.running ? '実行中…' : 'テスト実行 (dry-run)'}
                                        </button>
                                        {detail.rule.status === 'disabled' ? (
                                          <button type="button" style={S.smallBtn} disabled={actionBusy}
                                            onClick={(e) => { e.stopPropagation(); doEnable(); }}>再有効化</button>
                                        ) : armDisable ? (
                                          <>
                                            <button type="button"
                                              style={{ ...S.smallBtn, background: C.danger, borderColor: C.danger, color: '#fff' }}
                                              disabled={actionBusy}
                                              onClick={(e) => { e.stopPropagation(); doDisable(); }}>
                                              無効化を確定 (以後この提案は停止)
                                            </button>
                                            <button type="button" style={S.smallBtn}
                                              onClick={(e) => { e.stopPropagation(); setArmDisable(false); }}>取消</button>
                                          </>
                                        ) : (
                                          <button type="button"
                                            style={{ ...S.smallBtn, color: C.danger, borderColor: '#e6b0ac' }}
                                            onClick={(e) => { e.stopPropagation(); setArmDisable(true); }}>無効化</button>
                                        )}
                                      </div>

                                      {/* knowledge domain: 参考知識であることの明示 */}
                                      {detail.rule.domain === 'knowledge' && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, padding: '8px 12px', border: `1px solid ${C.blueLine}`, borderRadius: 5, background: C.blueTint, color: '#33699e', fontSize: 12, fontWeight: 700 }}>
                                          <MdInfoOutline size={15} style={{ flex: '0 0 auto' }} /> {KNOWLEDGE_NOTE}
                                        </div>
                                      )}

                                      {/* ① 内容 */}
                                      <div style={S.secHead}><MdEditNote size={15} /> 1. 内容 (対象条件 / 帰結)</div>
                                      {!edit && (
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 10 }}>
                                          <div style={S.box}>
                                            <div style={{ padding: '6px 10px', borderBottom: `1px solid ${C.line}`, background: C.blueTint, color: '#33699e', fontSize: 11, fontWeight: 800 }}>対象条件 (match_keys)</div>
                                            <KVTable obj={parseJsonSafe(detail.rule.match_keys_json)} maps={maps} emptyText="(条件なし)" />
                                          </div>
                                          <div style={S.box}>
                                            <div style={{ padding: '6px 10px', borderBottom: `1px solid ${C.line}`, background: C.orangeTint, color: '#a46922', fontSize: 11, fontWeight: 800 }}>帰結 (payload — 勘定・部門・税は名称解決済)</div>
                                            <KVTable obj={parseJsonSafe(detail.rule.payload_json)} maps={maps} emptyText="(帰結なし)" />
                                          </div>
                                        </div>
                                      )}
                                      {!edit && (
                                        <div style={{ marginTop: 8 }}>
                                          <button type="button" style={S.smallBtn} onClick={(e) => { e.stopPropagation(); beginEdit(); }}>
                                            編集 (構造化フォーム)
                                          </button>
                                        </div>
                                      )}
                                      {edit && (
                                        <div style={{ ...S.box, padding: 12 }} onClick={(e) => e.stopPropagation()}>
                                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14 }}>
                                            {([['対象条件 (match_keys)', 'matchFields'], ['帰結 (payload)', 'payloadFields']] as const).map(([label, fkey]) => (
                                              <div key={fkey}>
                                                <div style={{ marginBottom: 6, color: '#344b62', fontSize: 11, fontWeight: 800 }}>{label}</div>
                                                {edit[fkey].length === 0 && <div style={{ color: C.sub, fontSize: 12 }}>(項目なし)</div>}
                                                {edit[fkey].map((f, idx) => (
                                                  <div key={f.key} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                                                    <span style={{ flex: '0 0 170px', color: C.sub, fontSize: 11, fontWeight: 800, fontFamily: NUM, overflow: 'hidden', textOverflow: 'ellipsis' }} title={f.key}>{f.key}</span>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                      <FieldInput field={f} maps={maps} onChange={(v) => {
                                                        setEdit((prev) => {
                                                          if (!prev) return prev;
                                                          const arr = prev[fkey].slice();
                                                          arr[idx] = { ...arr[idx], value: v };
                                                          return { ...prev, [fkey]: arr };
                                                        });
                                                      }} />
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                            ))}
                                          </div>
                                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
                                            <span style={S.filterLabel}>変更理由 (必須・監査記録)</span>
                                            <input type="text" style={{ ...S.formInput, width: 340 }} value={edit.reason}
                                              placeholder="例: ガス代の部門を本部固定に修正"
                                              onChange={(e) => setEdit((prev) => prev && ({ ...prev, reason: e.target.value }))} />
                                            <Button appearance="primary" small disabled={edit.saving} onClick={saveEdit}>
                                              {edit.saving ? <InlineSpinner isLoading /> : null} 変更を適用 (v{detail.rule.version} 起点)
                                            </Button>
                                            <button type="button" style={S.smallBtn} onClick={() => setEdit(null)}>キャンセル</button>
                                            {edit.conflict && (
                                              <button type="button" style={{ ...S.smallBtn, color: '#33699e', borderColor: C.blueLine }} onClick={reloadAfterConflict}>
                                                最新を再読込
                                              </button>
                                            )}
                                          </div>
                                          {edit.error && <div style={{ marginTop: 8, color: C.danger, fontSize: 12, fontWeight: 700 }}>{edit.error}</div>}
                                        </div>
                                      )}

                                      {/* ② 由来 */}
                                      <div style={S.secHead}><MdHistory size={15} /> 2. 由来 (provenance / 変更履歴)</div>
                                      <div style={S.box}>
                                        {provenanceObj && typeof provenanceObj === 'object' && (
                                          <div style={{ borderBottom: `1px solid ${C.line}` }}>
                                            <KVTable obj={provenanceObj} maps={maps} emptyText="(由来情報なし)" />
                                          </div>
                                        )}
                                        {(!provenanceObj || typeof provenanceObj !== 'object') && (
                                          <div style={{ padding: '8px 12px', color: C.sub, fontSize: 12, borderBottom: `1px solid ${C.line}` }}>
                                            由来情報 (provenance) は未記録です
                                          </div>
                                        )}
                                        {(detail.versions || []).slice().sort((a, b) => Number(b.version) - Number(a.version)).map((v, i) => (
                                          <div key={`${v.version}_${i}`} style={S.timelineItem}>
                                            <span style={{ ...S.chip, flex: '0 0 auto' }}>v{v.version}</span>
                                            <span style={{ ...S.mono, flex: '0 0 auto', color: C.sub, fontSize: 11 }}>{fmtTs(v.ts)}</span>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                              <div style={{ fontWeight: 700 }}>{v.reason || '(理由記載なし)'}</div>
                                              <div style={{ color: C.sub, fontSize: 11 }}>
                                                変更者: {v.changed_by || '—'}
                                                {v.diff_json ? ` ／ 差分: ${trunc(String(v.diff_json), 160)}` : ''}
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                        <div style={{ ...S.timelineItem, borderBottom: 0 }}>
                                          <span style={{ ...S.chip, flex: '0 0 auto' }}>v1</span>
                                          <span style={{ ...S.mono, flex: '0 0 auto', color: C.sub, fontSize: 11 }}>{fmtTs(detail.rule.created_at)}</span>
                                          <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: 700 }}>作成 ({ORIGIN_LABEL[detail.rule.origin || ''] || detail.rule.origin || '—'})</div>
                                            <div style={{ color: C.sub, fontSize: 11 }}>作成者: {detail.rule.created_by || '—'}</div>
                                          </div>
                                        </div>
                                      </div>

                                      {/* ③ 成績 + 適用先 */}
                                      <div style={S.secHead}><MdCheckCircle size={15} /> 3. 成績と適用先</div>
                                      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 8, fontFamily: NUM, fontSize: 12.5 }}>
                                        <span>標本 <b>{Number(detail.rule.sample_count || 0)}</b>件</span>
                                        <span style={{ color: C.good }}>承認 <b>{Number(detail.rule.approve_count || 0)}</b></span>
                                        <span style={{ color: Number(detail.rule.reject_count || 0) > 0 ? C.danger : C.sub }}>差戻し <b>{Number(detail.rule.reject_count || 0)}</b></span>
                                        <span>適用記録 <b>{(detail.applications || []).length}</b>件</span>
                                      </div>
                                      <div style={S.box}>
                                        {(detail.applications || []).length === 0 && (
                                          <div style={{ padding: '10px 12px', color: C.sub, fontSize: 12 }}>適用記録はまだありません</div>
                                        )}
                                        {(detail.applications || []).length > 0 && (
                                          <div style={{ overflowX: 'auto' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                              <thead><tr>
                                                <th style={S.th}>適用先</th><th style={S.th}>ID</th><th style={S.th}>結果</th><th style={S.th}>日時</th><th style={S.th}>job</th>
                                              </tr></thead>
                                              <tbody>
                                                {(detail.applications || []).slice(0, 50).map((a, i) => {
                                                  const rm = RESULT_META[String(a.result)] || null;
                                                  return (
                                                    <tr key={i}>
                                                      <td style={S.td}>{a.applied_to || '—'}</td>
                                                      <td style={{ ...S.td, fontFamily: NUM }}>{a.applied_to_id || '—'}</td>
                                                      <td style={S.td}>{rm ? <span style={S.badge(rm.bg, rm.fg, rm.bd)}>{rm.label}</span> : (a.result || '—')}</td>
                                                      <td style={{ ...S.td, fontFamily: NUM, fontSize: 11 }}>{fmtTs(a.ts)}</td>
                                                      <td style={{ ...S.td, fontFamily: NUM, fontSize: 11 }}>{a.job_id || '—'}</td>
                                                    </tr>
                                                  );
                                                })}
                                              </tbody>
                                            </table>
                                            {(detail.applications || []).length > 50 && (
                                              <div style={{ padding: '6px 10px', color: C.sub, fontSize: 11 }}>ほか {(detail.applications || []).length - 50} 件 (先頭50件のみ表示)</div>
                                            )}
                                          </div>
                                        )}
                                      </div>

                                      {/* ④ テスト実行結果 */}
                                      <div style={S.secHead}><MdScience size={15} /> 4. テスト実行 (dry-run — freee への書込なし)</div>
                                      <div style={S.box}>
                                        {!dryRun.result && !dryRun.error && !dryRun.running && (
                                          <div style={{ padding: '10px 12px', color: C.sub, fontSize: 12 }}>
                                            上部の「テスト実行 (dry-run)」で、このルールが現在のデータに何件マッチし、どんな提案を生成するかを確認できます
                                          </div>
                                        )}
                                        {dryRun.running && <div style={{ padding: '10px 12px', color: C.sub, fontSize: 12 }}><InlineSpinner isLoading /> dry-run 実行中…</div>}
                                        {dryRun.error && <div style={{ padding: '10px 12px', color: C.danger, fontSize: 12, fontWeight: 700 }}>dry-run 失敗: {dryRun.error}</div>}
                                        {dryRun.result && dryRun.result.dry_run_unsupported && (
                                          <div style={{ padding: '10px 12px', color: C.warn, fontSize: 12, fontWeight: 700, background: C.warnBg }}>
                                            このルール種別 ({DOMAIN_LABEL[detail.rule.domain] || detail.rule.domain}) の dry-run は未対応です
                                          </div>
                                        )}
                                        {dryRun.result && !dryRun.result.dry_run_unsupported && (
                                          <>
                                            <div style={{ padding: '8px 12px', borderBottom: `1px solid ${C.line}`, fontSize: 12.5, fontWeight: 800 }}>
                                              マッチ <span style={{ ...S.mono, color: '#234e79' }}>{Number(dryRun.result.matched ?? (dryRun.result.preview || []).length)}</span> 件
                                            </div>
                                            <GenericTable rows={Array.isArray(dryRun.result.preview) ? dryRun.result.preview : []} />
                                          </>
                                        )}
                                      </div>

                                      {/* ⑤ feedback */}
                                      <div style={S.secHead}><MdWarningAmber size={15} /> 5. このルールへの評価 (学習の自己修復)</div>
                                      <div style={{ ...S.box, padding: 12 }}>
                                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
                                          <input type="text" style={{ ...S.formInput, width: 320 }} value={fbComment}
                                            placeholder="コメント (任意) 例: 4月以降は部門が変わった"
                                            onChange={(e) => setFbComment(e.target.value)} />
                                          <button type="button" style={{ ...S.smallBtn, color: C.good, borderColor: '#9fdbc7' }}
                                            disabled={fbSending} onClick={() => sendFeedback('correct')}>このルールは正しい</button>
                                          <button type="button" style={{ ...S.smallBtn, color: C.danger, borderColor: '#e6b0ac' }}
                                            disabled={fbSending} onClick={() => sendFeedback('wrong')}>このルールは誤り</button>
                                          {fbSending && <InlineSpinner isLoading />}
                                        </div>
                                        {(detail.feedback || []).length > 0 && (
                                          <div style={{ marginTop: 10, borderTop: `1px dashed ${C.line}`, paddingTop: 8 }}>
                                            {(detail.feedback || []).slice(0, 20).map((f, i) => {
                                              const vm = VERDICT_META[String(f.verdict)] || null;
                                              return (
                                                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '3px 0', fontSize: 12 }}>
                                                  {vm ? <span style={S.badge(vm.bg, vm.fg, vm.bd)}>{vm.label}</span> : <span style={S.chip}>{f.verdict}</span>}
                                                  <span style={{ color: C.sub, fontSize: 11 }}>{f.source || ''}</span>
                                                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.comment || ''}</span>
                                                  <span style={{ ...S.mono, color: C.sub, fontSize: 11, flex: '0 0 auto' }}>{f.by || ''} {fmtTs(f.ts)}</span>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>

                                      {/* ⑦ raw JSON */}
                                      <div style={{ marginTop: 14 }}>
                                        <button type="button" style={S.smallBtn} onClick={(e) => { e.stopPropagation(); setShowRaw((v) => !v); }}>
                                          {showRaw ? 'raw JSON を閉じる' : 'raw JSON を表示'}
                                        </button>
                                        <span style={{ marginLeft: 8, color: C.sub, fontSize: 11 }}>
                                          写像元テーブル: <b style={S.mono as React.CSSProperties}>{detail.rule.source_table || '(rules 直接)'}</b>
                                        </span>
                                        {showRaw && (
                                          <div style={{ ...S.box, marginTop: 6 }}>
                                            <pre style={S.rawPre}>{JSON.stringify(detail.rule, null, 2)}</pre>
                                          </div>
                                        )}
                                      </div>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
                {rows.length > MAX_ROWS && (
                  <div style={{ padding: '8px 12px', color: C.sub, fontSize: 11, fontWeight: 700, borderTop: `1px solid ${C.line}` }}>
                    表示は先頭 {MAX_ROWS} 件までです (全 {rows.length.toLocaleString('ja-JP')} 件)。ドメインタブ・検索で絞り込んでください
                  </div>
                )}
              </div>
            )}
          </section>
        </section>
      </Container>

      {/* toast */}
      {toast && (
        <div style={{
          ...S.toast,
          background: toast.kind === 'good' ? C.goodBg : C.dangerBg,
          color: toast.kind === 'good' ? C.good : C.danger,
          borderColor: toast.kind === 'good' ? '#9fdbc7' : '#e6b0ac',
        }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <MdMenuBook size={14} /> {toast.msg}
          </span>
        </div>
      )}
    </div>
  );
};

export default RulebookPage;
